import React, { useState } from 'react';
import Layout from '../components/Layout';
import {
  TOTALS, FLAVORS, EFFECTS, TOP_EFFECTS_BY_STRAIN,
  CONDITIONS, SYMPTOMS, NEGATIVES,
  TERPENES, CANNABINOIDS, CATEGORIES,
} from '../data/strainAggregates';
import '../styles/StrainInsights.css';

// ─── Color Helpers ────────────────────────────────────────────────────────────

function scoreColor(score) {
  if (score >= 0.8)  return '#22c55e';
  if (score >= 0.5)  return '#4ade80';
  if (score >= 0.2)  return '#86efac';
  if (score >= 0)    return '#a3e635';
  if (score >= -0.3) return '#fbbf24';
  if (score >= -0.6) return '#f97316';
  return '#ef4444';
}

const PALETTE = [
  '#4ade80', '#22d3ee', '#a78bfa', '#fbbf24',
  '#f472b6', '#60a5fa', '#fb923c', '#34d399',
  '#e879f9', '#94a3b8',
];

// ─── Horizontal Bar Chart ─────────────────────────────────────────────────────
// data: [{name, value, score?}]   colorFn: name => css color string

function HBarChart({ data, colorFn, maxOverride, labelWidth = 130, showValue = true }) {
  const maxVal = maxOverride || Math.max(...data.map(d => d.value));
  return (
    <div className="hbar-chart">
      {data.map((d, i) => {
        const pct = maxVal > 0 ? (d.value / maxVal) * 100 : 0;
        const color = colorFn ? colorFn(d) : PALETTE[i % PALETTE.length];
        return (
          <div className="hbar-row" key={d.name}>
            <span
              className="hbar-label"
              style={{ flex: `0 0 ${labelWidth}px` }}
              title={d.name}
            >
              {d.name}
            </span>
            <div className="hbar-track">
              <div
                className="hbar-fill"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            {showValue && (
              <span className="hbar-value">
                {d.value >= 1000
                  ? (d.value / 1000).toFixed(1) + 'k'
                  : d.value}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Donut Chart ──────────────────────────────────────────────────────────────
// data: [{name, value, color}]

function buildArcs(data, cx, cy, R, r) {
  const total = data.reduce((s, d) => s + d.value, 0);
  let angle = -Math.PI / 2;
  return data.map(d => {
    const sweep = (d.value / total) * 2 * Math.PI;
    const a1 = angle, a2 = angle + sweep;
    const la = sweep > Math.PI ? 1 : 0;

    const ox1 = cx + R * Math.cos(a1), oy1 = cy + R * Math.sin(a1);
    const ox2 = cx + R * Math.cos(a2), oy2 = cy + R * Math.sin(a2);
    const ix1 = cx + r * Math.cos(a1), iy1 = cy + r * Math.sin(a1);
    const ix2 = cx + r * Math.cos(a2), iy2 = cy + r * Math.sin(a2);

    const path = [
      `M ${ix1.toFixed(2)} ${iy1.toFixed(2)}`,
      `L ${ox1.toFixed(2)} ${oy1.toFixed(2)}`,
      `A ${R} ${R} 0 ${la} 1 ${ox2.toFixed(2)} ${oy2.toFixed(2)}`,
      `L ${ix2.toFixed(2)} ${iy2.toFixed(2)}`,
      `A ${r} ${r} 0 ${la} 0 ${ix1.toFixed(2)} ${iy1.toFixed(2)}`,
      'Z',
    ].join(' ');

    angle = a2;
    return { ...d, path, pct: d.value / total };
  });
}

function DonutChart({ data, centerLabel, size = 160 }) {
  const cx = size / 2, cy = size / 2;
  const R = size * 0.44, r = size * 0.26;
  const arcs = buildArcs(data, cx, cy, R, r);
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="donut-wrap">
      <svg viewBox={`0 0 ${size} ${size}`} style={{ flex: `0 0 ${size}px`, width: size, height: size }}>
        {arcs.map((arc, i) => (
          <path key={i} d={arc.path} fill={arc.color} opacity={0.9}>
            <title>{arc.name}: {arc.value.toLocaleString()} ({(arc.pct * 100).toFixed(1)}%)</title>
          </path>
        ))}
        {centerLabel && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
            fill="#e2e8f0" fontSize={13} fontWeight="700">{centerLabel}</text>
        )}
      </svg>
      <div className="donut-legend">
        {data.map((d, i) => (
          <div className="donut-legend-item" key={i}>
            <span className="donut-swatch" style={{ background: d.color }} />
            <span>{d.name}</span>
            <span className="donut-pct">{((d.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Bubble / Scatter Chart ───────────────────────────────────────────────────
// data: [{name, x (score), y (votes), r (appearances)}]

function BubbleChart({ data }) {
  const W = 560, H = 340;
  const PAD = { l: 50, r: 20, t: 20, b: 40 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;

  const minX = -1.05, maxX = 1.1;
  const maxY = Math.max(...data.map(d => d.y));
  const maxR = Math.max(...data.map(d => d.r));

  const scaleX = v => PAD.l + ((v - minX) / (maxX - minX)) * plotW;
  const scaleY = v => PAD.t + plotH - (v / maxY) * plotH;
  const scaleR = v => 4 + (v / maxR) * 16;

  const yTicks = [0, 20000, 40000, 60000, 80000];
  const xTicks = [-1, -0.5, 0, 0.5, 1];

  return (
    <div className="bubble-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`}>
        {/* Grid lines */}
        {yTicks.map(v => (
          <line key={v} x1={PAD.l} y1={scaleY(v)} x2={W - PAD.r} y2={scaleY(v)}
            stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
        ))}
        {xTicks.map(v => (
          <line key={v} x1={scaleX(v)} y1={PAD.t} x2={scaleX(v)} y2={H - PAD.b}
            stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
        ))}
        {/* Zero‑score axis */}
        <line x1={scaleX(0)} y1={PAD.t} x2={scaleX(0)} y2={H - PAD.b}
          stroke="rgba(74,222,128,0.25)" strokeWidth={1} strokeDasharray="4 3" />

        {/* Y axis labels */}
        {yTicks.filter(v => v > 0).map(v => (
          <text key={v} x={PAD.l - 6} y={scaleY(v)} textAnchor="end"
            dominantBaseline="middle" fill="#475569" fontSize={9}>
            {v / 1000}k
          </text>
        ))}
        {/* X axis labels */}
        {xTicks.map(v => (
          <text key={v} x={scaleX(v)} y={H - PAD.b + 14} textAnchor="middle"
            fill="#475569" fontSize={9}>{v}</text>
        ))}

        {/* Axis labels */}
        <text x={W / 2} y={H - 4} textAnchor="middle" fill="#475569" fontSize={10}>
          Sentiment Score →
        </text>
        <text x={12} y={H / 2} textAnchor="middle" fill="#475569" fontSize={10}
          transform={`rotate(-90, 12, ${H / 2})`}>
          Total Votes ↑
        </text>

        {/* Bubbles */}
        {data.map((d, i) => {
          const cx = scaleX(d.x), cy = scaleY(d.y);
          const rad = scaleR(d.r);
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={rad}
                fill={scoreColor(d.x)} opacity={0.75}
                stroke={scoreColor(d.x)} strokeWidth={1} strokeOpacity={0.9}
              >
                <title>{d.name}{'\n'}Votes: {d.y.toLocaleString()}{'\n'}Score: {d.x}{'\n'}Appears in: {d.r.toLocaleString()} strains</title>
              </circle>
              {rad > 9 && (
                <text cx={cx} x={cx} y={cy + rad + 10} textAnchor="middle"
                  fill="#94a3b8" fontSize={8}>{d.name}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Radar Chart ──────────────────────────────────────────────────────────────
// data: [{name, value}]

function RadarChart({ data, color = '#4ade80' }) {
  const CX = 200, CY = 190, R = 130;
  const n = data.length;
  const maxVal = Math.max(...data.map(d => d.value));

  const axisAngle = i => (i / n) * 2 * Math.PI - Math.PI / 2;

  const outerPt = (i) => {
    const a = axisAngle(i);
    return [CX + R * Math.cos(a), CY + R * Math.sin(a)];
  };

  const dataPt = (v, i) => {
    const a = axisAngle(i);
    const r = (v / maxVal) * R;
    return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
  };

  const labelPt = (i) => {
    const a = axisAngle(i);
    return [CX + (R + 26) * Math.cos(a), CY + (R + 26) * Math.sin(a)];
  };

  const rings = [0.25, 0.5, 0.75, 1.0];

  const polyPath = pts =>
    pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ') + ' Z';

  const dataPts = data.map((d, i) => dataPt(d.value, i));
  const outerPts = data.map((_, i) => outerPt(i));

  return (
    <div className="radar-wrap">
      <svg viewBox="0 0 400 380">
        {/* Grid rings */}
        {rings.map((scale, ri) => {
          const pts = data.map((_, i) => {
            const a = axisAngle(i);
            return [CX + R * scale * Math.cos(a), CY + R * scale * Math.sin(a)];
          });
          return (
            <path key={ri} d={polyPath(pts)}
              fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
          );
        })}

        {/* Axis spokes */}
        {outerPts.map(([x, y], i) => (
          <line key={i} x1={CX} y1={CY} x2={x.toFixed(1)} y2={y.toFixed(1)}
            stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
        ))}

        {/* Data polygon */}
        <path d={polyPath(dataPts)}
          fill={color + '28'} stroke={color} strokeWidth={2} strokeLinejoin="round" />

        {/* Data dots */}
        {dataPts.map(([x, y], i) => (
          <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r={4}
            fill={color} stroke="#0a1a0e" strokeWidth={1.5}>
            <title>{data[i].name}: {data[i].value.toFixed(4)}</title>
          </circle>
        ))}

        {/* Labels */}
        {data.map((d, i) => {
          const [lx, ly] = labelPt(i);
          return (
            <text key={i} x={lx.toFixed(1)} y={ly.toFixed(1)}
              textAnchor="middle" dominantBaseline="middle"
              fill="#94a3b8" fontSize={11} fontWeight="500">
              {d.name}
            </text>
          );
        })}

        {/* Center label */}
        <text x={CX} y={CY} textAnchor="middle" dominantBaseline="middle"
          fill="#475569" fontSize={10}>Avg Score</text>
      </svg>
    </div>
  );
}

// ─── Score Legend ─────────────────────────────────────────────────────────────

function ScoreLegend() {
  const items = [
    { label: 'Strongly positive (≥0.8)', color: '#22c55e' },
    { label: 'Positive (0.2–0.8)',        color: '#4ade80' },
    { label: 'Neutral (−0.2–0.2)',        color: '#a3e635' },
    { label: 'Mild negative',             color: '#fbbf24' },
    { label: 'Negative',                  color: '#ef4444' },
  ];
  return (
    <div className="score-legend">
      {items.map(it => (
        <span className="score-legend-item" key={it.label}>
          <span className="score-swatch" style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ═══ TABS ════════════════════════════════════════════════════════════════════

// ─── FLAVORS TAB ──────────────────────────────────────────────────────────────
function FlavorsTab() {
  const topFlavors = FLAVORS.slice(0, 20);
  const donutData = FLAVORS.slice(0, 8).map((f, i) => ({
    name: f.name,
    value: f.votes,
    color: PALETTE[i],
  }));

  const bubbleData = FLAVORS.map(f => ({
    name: f.name,
    x: f.score,
    y: f.votes,
    r: f.appearances,
  }));

  return (
    <div>
      <p className="section-subtitle">
        Aggregate across 7,647 strains — 47 unique flavors. Bar color = community sentiment score.
      </p>
      <ScoreLegend />

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Top 20 Flavors — Total Vote Count</div>
          <HBarChart
            data={topFlavors.map(f => ({ name: f.name, value: f.votes, score: f.score }))}
            colorFn={d => scoreColor(d.score)}
            labelWidth={110}
          />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Vote Share — Top 8 Flavors</div>
          <DonutChart data={donutData} centerLabel="Flavors" />
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card chart-card-wide">
          <div className="chart-card-title">
            Flavor Universe — Vote Count vs Sentiment Score
            <span style={{ fontWeight: 400, marginLeft: 8, color: '#475569' }}>
              (bubble size = appearances across strains)
            </span>
          </div>
          <BubbleChart data={bubbleData} />
        </div>
      </div>
    </div>
  );
}

// ─── EFFECTS TAB ──────────────────────────────────────────────────────────────
function EffectsTab() {
  const effectsForBars = [...EFFECTS].sort((a, b) => b.votes - a.votes);
  const effectScoreBars = [...EFFECTS].sort((a, b) => b.score - a.score);

  const donutEffects = TOP_EFFECTS_BY_STRAIN.map((e, i) => ({
    name: e.name,
    value: e.count,
    color: PALETTE[i % PALETTE.length],
  }));

  const catDonut = CATEGORIES.map(c => ({ ...c, value: c.count }));

  return (
    <div>
      <p className="section-subtitle">
        All 13 effect types across the corpus. All effects have positive average sentiment scores.
      </p>

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Effects — Total Vote Count</div>
          <HBarChart
            data={effectsForBars.map(e => ({ name: e.name, value: e.votes, score: e.score }))}
            colorFn={d => scoreColor(d.score)}
            labelWidth={110}
          />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Sentiment Score by Effect (higher = more positively received)</div>
          <HBarChart
            data={effectScoreBars.map(e => ({ name: e.name, value: Math.round(e.score * 1000), score: e.score }))}
            colorFn={d => scoreColor(d.score)}
            labelWidth={110}
            showValue={false}
          />
          <div style={{ marginTop: 14 }}>
            {effectScoreBars.map(e => (
              <div key={e.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#475569', padding: '1px 0' }}>
                <span>{e.name}</span>
                <span style={{ color: scoreColor(e.score) }}>{e.score.toFixed(3)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Dominant Effect per Strain (strain count)</div>
          <DonutChart data={donutEffects} centerLabel="Effects" size={150} />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Strain Category Split</div>
          <DonutChart data={catDonut} centerLabel="7,647" size={150} />
        </div>
      </div>
    </div>
  );
}

// ─── MEDICAL RELIEF TAB ───────────────────────────────────────────────────────
function MedicalTab() {
  return (
    <div>
      <p className="section-subtitle">
        Community-reported relief for medical conditions and symptoms.
        Score = sentiment (positive = widely effective; negative = limited relief reported).
      </p>
      <ScoreLegend />

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Medical Conditions — Top 15 by Vote Count</div>
          <HBarChart
            data={CONDITIONS.map(c => ({ name: c.name, value: c.votes, score: c.score }))}
            colorFn={d => scoreColor(d.score)}
            labelWidth={145}
          />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Symptoms Helped — All 14</div>
          <HBarChart
            data={SYMPTOMS.map(s => ({ name: s.name, value: s.votes, score: s.score }))}
            colorFn={d => scoreColor(d.score)}
            labelWidth={130}
          />
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card chart-card-wide">
          <div className="chart-card-title">Conditions — Condition Score vs Vote Volume</div>
          <BubbleChart
            data={CONDITIONS.map(c => ({
              name: c.name,
              x: Math.max(-1.1, c.score),
              y: c.votes,
              r: c.votes,
            }))}
          />
        </div>
      </div>
    </div>
  );
}

// ─── CANNABINOIDS TAB ─────────────────────────────────────────────────────────
function CannabinoidsTab() {
  const maxMean = Math.max(...CANNABINOIDS.map(c => c.mean));

  return (
    <div>
      <p className="section-subtitle">
        Percentile-50 cannabinoid values (non-zero strains only). THC dominates;
        CBD and CBG are present in a smaller subset.
      </p>

      <div className="cannab-grid">
        {CANNABINOIDS.map(c => (
          <div className="cannab-stat" key={c.name}
            style={{ borderColor: c.color + '33' }}>
            <span className="cannab-stat-value" style={{ color: c.color }}>
              {c.mean.toFixed(1)}%
            </span>
            <span className="cannab-stat-label">{c.displayName}</span>
            <span className="cannab-stat-sub">
              med {c.median.toFixed(1)}% | max {c.max}%
            </span>
            <span className="cannab-stat-sub">{c.strains.toLocaleString()} strains</span>
          </div>
        ))}
      </div>

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Mean % by Cannabinoid (non-zero strains)</div>
          <HBarChart
            data={CANNABINOIDS.map(c => ({ name: c.displayName, value: Math.round(c.mean * 100), score: 0.5 }))}
            colorFn={(d) => CANNABINOIDS.find(c => c.displayName === d.name)?.color || '#4ade80'}
            maxOverride={Math.round(maxMean * 100)}
            labelWidth={60}
          />
          <p style={{ fontSize: '0.72rem', color: '#475569', marginTop: 12 }}>
            * Values are percentile-50 (median) reported % from strain data. Bar width = relative to THC.
          </p>
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Strain Coverage by Cannabinoid</div>
          <HBarChart
            data={CANNABINOIDS.map(c => ({ name: c.displayName, value: c.strains }))}
            colorFn={(d) => CANNABINOIDS.find(c => c.displayName === d.name)?.color || '#4ade80'}
            labelWidth={60}
          />
          <p style={{ fontSize: '0.72rem', color: '#475569', marginTop: 12 }}>
            * Number of strains with a non-zero measured value for that cannabinoid.
          </p>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card chart-card-wide">
          <div className="chart-card-title">Typical Strain Profile — Relative Cannabinoid Proportions</div>
          <div style={{ padding: '8px 0' }}>
            {CANNABINOIDS.map(c => {
              const widthPct = (c.mean / maxMean) * 100;
              return (
                <div key={c.name} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                    <span style={{ color: c.color, fontWeight: 600 }}>{c.displayName}</span>
                    <span style={{ color: '#64748b' }}>avg {c.mean.toFixed(2)}% &nbsp;|&nbsp; median {c.median}% &nbsp;|&nbsp; max {c.max}%</span>
                  </div>
                  <div style={{ height: 22, background: 'rgba(255,255,255,0.04)', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{
                      width: `${widthPct}%`, height: '100%',
                      background: `linear-gradient(90deg, ${c.color}cc, ${c.color})`,
                      borderRadius: 5,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TERPENES TAB ─────────────────────────────────────────────────────────────
function TerpenesTab() {
  const radarData = TERPENES.map(t => ({ name: t.name, value: t.avgScore }));
  const maxScore = Math.max(...TERPENES.map(t => t.avgScore));

  return (
    <div>
      <p className="section-subtitle">
        8 terpenes tracked across 3,598 strains. Scores reflect relative prominence/presence.
      </p>

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Terpene Radar — Average Score Profile</div>
          <RadarChart data={radarData} color="#a78bfa" />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Terpene Rankings — Average Score</div>
          <HBarChart
            data={TERPENES.map(t => ({ name: t.name, value: Math.round(t.avgScore * 10000) }))}
            colorFn={() => '#a78bfa'}
            labelWidth={120}
            showValue={false}
          />
          <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
            {TERPENES.map(t => (
              <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', padding: '2px 0' }}>
                <span style={{ color: '#a78bfa', fontWeight: 600 }}>{t.name}</span>
                <span style={{ color: '#64748b' }}>{t.avgScore.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-card chart-card-wide">
          <div className="chart-card-title">Terpene Details</div>
          <table className="terp-table">
            <thead>
              <tr>
                <th>Terpene</th>
                <th>Avg Score</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {TERPENES.map(t => (
                <tr key={t.name}>
                  <td className="terp-name">{t.name}</td>
                  <td>
                    <div className="terp-score-bar-wrap">
                      <div className="terp-score-bar">
                        <div className="terp-score-fill"
                          style={{ width: `${(t.avgScore / maxScore) * 100}%` }} />
                      </div>
                      <span className="terp-score-val">{t.avgScore.toFixed(3)}</span>
                    </div>
                  </td>
                  <td>{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── SIDE EFFECTS TAB ─────────────────────────────────────────────────────────
function SideEffectsTab() {
  const negColors = ['#ef4444', '#f97316', '#fbbf24', '#fb923c', '#f87171', '#fca5a5'];
  const donutData = NEGATIVES.map((n, i) => ({
    name: n.name, value: n.votes, color: negColors[i],
  }));

  return (
    <div>
      <p className="section-subtitle">
        Community-reported negative side effects across 7,647 strains. Only 6 unique types recorded.
      </p>

      <div className="chart-grid">
        <div className="chart-card">
          <div className="chart-card-title">Side Effect Vote Distribution</div>
          <DonutChart data={donutData} centerLabel="Side FX" size={160} />
        </div>

        <div className="chart-card">
          <div className="chart-card-title">Side Effects — Total Vote Count</div>
          <HBarChart
            data={NEGATIVES.map((n, i) => ({ name: n.name, value: n.votes }))}
            colorFn={(d, i) => negColors[NEGATIVES.findIndex(n => n.name === d.name)]}
            labelWidth={100}
          />
          <p style={{ fontSize: '0.72rem', color: '#475569', marginTop: 14 }}>
            Dry mouth and dry eyes account for ~70% of all reported negative votes.
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══ MAIN COMPONENT ══════════════════════════════════════════════════════════

const TABS = [
  { key: 'flavors',      label: 'Flavors',      component: FlavorsTab },
  { key: 'effects',      label: 'Effects',       component: EffectsTab },
  { key: 'medical',      label: 'Medical Relief',component: MedicalTab },
  { key: 'cannabinoids', label: 'Cannabinoids',  component: CannabinoidsTab },
  { key: 'terpenes',     label: 'Terpenes',      component: TerpenesTab },
  { key: 'sideeffects',  label: 'Side Effects',  component: SideEffectsTab },
];

export default function StrainInsights() {
  const [activeTab, setActiveTab] = useState('flavors');
  const ActiveComponent = TABS.find(t => t.key === activeTab)?.component || FlavorsTab;

  return (
    <Layout>
      <div className="insights-page">
        <div className="insights-header">
          <h1 className="insights-title">Strain Data Insights</h1>
          <p className="insights-subtitle">
            Aggregate analysis across {TOTALS.strains.toLocaleString()} cannabis strains —
            flavors, effects, cannabinoids, terpenes & medical relief
          </p>

          <div className="insights-stats-row">
            <div className="insights-stat">
              <span className="insights-stat-value">{TOTALS.strains.toLocaleString()}</span>
              <span className="insights-stat-label">Strains</span>
            </div>
            <div className="insights-stat">
              <span className="insights-stat-value">{(TOTALS.reviews / 1000).toFixed(0)}k</span>
              <span className="insights-stat-label">Total Reviews</span>
            </div>
            <div className="insights-stat">
              <span className="insights-stat-value">{(TOTALS.followers / 1000000).toFixed(1)}M</span>
              <span className="insights-stat-label">Total Followers</span>
            </div>
            <div className="insights-stat">
              <span className="insights-stat-value">{TOTALS.meanRating}</span>
              <span className="insights-stat-label">Avg Rating</span>
            </div>
            <div className="insights-stat">
              <span className="insights-stat-value">47</span>
              <span className="insights-stat-label">Flavor Types</span>
            </div>
            <div className="insights-stat">
              <span className="insights-stat-value">8</span>
              <span className="insights-stat-label">Terpenes</span>
            </div>
          </div>
        </div>

        <div className="insights-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="insights-body">
          <ActiveComponent />
        </div>
      </div>
    </Layout>
  );
}
